import { useMemo } from 'react';
import {
  useFocusedIds,
  useIsFocusActive,
} from '@components/MainStage/focusOn.store';
import useFilteredCanvas from '@components/MainStage/hooks/useFilteredCanvas';
import {
  parentNodeTypes,
  SubLayersListByMode,
} from '@constants/canvas/general';
import { Modes } from '@constants/canvas/layers';
import { ExportLayerKeys } from '@constants/entities/reports';
import { useProject } from '@context/Project/ProjectProvider';
import useProjectData from '@hooks/useProjectData';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectCanvasNodesQuery } from '@store/services/nodes';
import { useGenerateNodesReportMutation } from '@store/services/reports';
import {
  getExportEntityIds,
  hideNodesOutsideFocus,
} from '@views/ReportsModel/exportScope';
import { getLayersReportFileName } from '@views/ReportsModel/helpers';
import useExportState from '@views/ReportsModel/hooks/useExportState';

const useExportLayers = (projectId: string, layers: ExportLayerKeys[]) => {
  const { isError, setError, fileContent, setFileContent } = useExportState();
  const [generateNodesReport, { isLoading, reset }] =
    useGenerateNodesReportMutation();

  const {
    toolbox: { mode },
  } = useProject();
  const [{ subscription }] = useProjectData();

  const { nodes } = useFilteredCanvas({ applyCollapse: false });
  const isFocusActive = useIsFocusActive();
  const focusedIds = useFocusedIds();

  const currentModeSubLayers = useMemo(
    () => new Set<string>(mode ? SubLayersListByMode[mode] : []),
    [mode],
  );

  const otherMode =
    mode === Modes.BusinessAlignment ? Modes.Controls : Modes.BusinessAlignment;

  const shouldFetchOtherMode =
    !!projectId &&
    !!mode &&
    mode !== Modes.RiskManagement &&
    !isFocusActive &&
    (subscription?.modes ?? []).includes(otherMode);

  const { data: otherModeData, isError: isOtherModeError } =
    useGetProjectCanvasNodesQuery(
      shouldFetchOtherMode ? { projectId, mode: otherMode } : skipToken,
    );

  const handleGenerate = async () => {
    try {
      if (shouldFetchOtherMode && (isOtherModeError || !otherModeData)) {
        setError(true);

        return;
      }

      const currentModeIds = getExportEntityIds(
        hideNodesOutsideFocus(nodes, isFocusActive, focusedIds),
      );

      const otherModeIds = isFocusActive
        ? []
        : (otherModeData?.nodes ?? []).reduce<string[]>((acc, node) => {
            const subLayer =
              parentNodeTypes[node.type as keyof typeof parentNodeTypes];

            if (subLayer && !currentModeSubLayers.has(subLayer))
              acc.push(node.id);

            return acc;
          }, []);

      const ids = Array.from(new Set([...currentModeIds, ...otherModeIds]));

      const excelData = await generateNodesReport({
        projectId,
        ids,
        layers,
      }).unwrap();

      setFileContent(excelData);
    } catch (e) {
      setError(true);
    }
  };

  const fileName = getLayersReportFileName(layers);

  return {
    isError,
    fileContent,
    isLoading,
    setFileContent,
    setError,
    handleGenerate,
    fileName,
    reset,
  };
};

export default useExportLayers;
